export interface ContentMetadata {
  documentType: string;
  data: any;
  createdAt: string;
  updatedAt?: string;
  version: number;
}

export interface DocumentContentMetadataDto {
  documentContentMetadataId: string;
  documentId: string;
  tenantId: string;
  documentType?: string;
  data?: string;
  version: number;
  createdAt: string;
  updatedAt?: string;
}

export interface DocumentContentMetadataCreateDto {
  documentId: string;
  tenantId: string;
  documentType?: string;
  data?: string;
}

export interface DocumentContentMetadataUpdateDto {
  documentId: string;
  documentType?: string;
  data?: string;
  updatedAt: string;
}

export interface DocumentResponse {
  id: string;
  documentId?: string;
  fileName: string;
  type: string;
  contentUri: string;
  createdAt: string;
  updatedAt?: string;
  version: number;
  contentMetadata?: ContentMetadata;
  ecmDocId?: number;
  extractionStatus?: string;
  extractionRecordId?: string;
}

export interface DocumentUpdateRequest {
  updatedFields?: Record<string, any>;
  // Allow any additional properties for flexibility
  [key: string]: any;
}

export class DocumentManagementSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param baseURL the base URL for the document analysis service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   *
   * @param endpoint
   * @param options
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<any> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.message}`);
    }

    return response;
  }

  /**
   * Uploads a document to the server
   * @param tenantId the tenant identifier
   * @param file a file to upload
   * @param convertTo string that indicates what document type to convert the file to (supported values: 'pdf')
   * @param documentSource optional document source
   * @param accessToken optional access token for authentication
   * @param ocrEngine
   * @returns a document response
   */
  async uploadDocument(
    tenantId: string,
    file: File,
    convertTo?: string,
    documentSource?: string,
    accessToken?: string | null,
    ocrEngine?: number
  ): Promise<DocumentResponse> {
    let endpoint = '/documents';

    const queryParams: string[] = [];

    if (convertTo) {
      queryParams.push(`convertTo=${encodeURIComponent(convertTo)}`);
    }

    if (documentSource) {
      queryParams.push(`documentSource=${encodeURIComponent(documentSource)}`);
    }

    if (ocrEngine) {
      queryParams.push(`ocrEngine=${encodeURIComponent(ocrEngine)}`);
    }

    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join('&')}`;
    }

    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {
      tenantId: tenantId
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers,
      body: formData
    });

    const result = await response.json();
    // Add documentId for compatibility with capture-app
    if (result.id && !result.documentId) {
      result.documentId = result.id;
    }
    return result;
  }

  /**
   * Retrieves a document by ID
   * @param tenantId the tenant identifier
   * @param id the document identifier
   * @param include optional include parameter to fetch additional data (e.g., 'contentmetadata')
   * @param accessToken optional access token for authentication
   * @returns a document response
   */
  async getDocument(
    tenantId: string,
    id: string,
    include?: string,
    accessToken?: string | null
  ): Promise<DocumentResponse> {
    let endpoint = `/documents/${id}`;
    if (include) {
      endpoint += `?include=${encodeURIComponent(include)}`;
    }

    const headers: Record<string, string> = {
      tenantId: tenantId
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers
    });

    const result = await response.json();
    // Add documentId for compatibility with capture-app
    if (result.id && !result.documentId) {
      result.documentId = result.id;
    }
    return result;
  }

  /**
   * Deletes a document by ID
   * @param tenantId the tenant identifier
   * @param id the document identifier
   * @param accessToken optional access token for authentication
   */
  async deleteDocument(tenantId: string, id: string, accessToken?: string | null): Promise<void> {
    const endpoint = `/documents/${id}`;

    const headers: Record<string, string> = {
      tenantId: tenantId
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    await this.fetchWrapper(endpoint, {
      method: 'DELETE',
      headers
    });
  }

  /**
   * Updates a document by ID
   * @param tenantId the tenant identifier
   * @param id the document identifier
   * @param request the update request containing document properties to update
   * @param accessToken optional access token for authentication
   */
  async updateDocument(
    tenantId: string,
    id: string,
    request: DocumentUpdateRequest,
    accessToken?: string | null
  ): Promise<void> {
    const endpoint = `/documents/${id}`;

    const headers: Record<string, string> = {
      tenantId: tenantId,
      'Content-Type': 'application/json'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    await this.fetchWrapper(endpoint, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request)
    });
  }

  /**
   * Get document content metadata by document ID
   * @param tenantId the tenant identifier
   * @param documentId the document identifier
   * @param accessToken optional access token for authentication
   * @returns document content metadata or null if not found
   */
  async getDocumentContentMetadata(
    tenantId: string,
    documentId: string,
    accessToken?: string | null
  ): Promise<DocumentContentMetadataDto | null> {
    const endpoint = `/documentcontentmetadata/${documentId}`;

    const headers: Record<string, string> = {
      tenantId: tenantId
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
      const response = await this.fetchWrapper(endpoint, {
        method: 'GET',
        headers
      });
      return await response.json();
    } catch (error: any) {
      if (error.message.includes('status: 404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create new document content metadata
   * @param tenantId the tenant identifier
   * @param data the document content metadata to create
   * @param accessToken optional access token for authentication
   * @returns the created document content metadata
   */
  async createDocumentContentMetadata(
    tenantId: string,
    data: DocumentContentMetadataCreateDto,
    accessToken?: string | null
  ): Promise<DocumentContentMetadataDto> {
    const endpoint = '/documentcontentmetadata';

    const headers: Record<string, string> = {
      tenantId: tenantId,
      'Content-Type': 'application/json'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });

    return await response.json();
  }

  /**
   * Update existing document content metadata
   * @param tenantId the tenant identifier
   * @param documentContentMetadataId the metadata identifier
   * @param data the updated metadata
   * @param accessToken optional access token for authentication
   */
  async updateDocumentContentMetadata(
    tenantId: string,
    documentContentMetadataId: string,
    data: DocumentContentMetadataUpdateDto,
    accessToken?: string | null
  ): Promise<void> {
    const endpoint = `/documentcontentmetadata/${documentContentMetadataId}`;

    const headers: Record<string, string> = {
      tenantId: tenantId,
      'Content-Type': 'application/json'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    await this.fetchWrapper(endpoint, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data)
    });
  }
}
